import { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import './ProductViewerCount.css';

const PULSE_INTERVAL_MS = 30 * 1000;
const DEFAULT_MESSAGE = '{count} people are viewing this product';

function formatMessage(template, count) {
  const normalized = typeof template === 'string' ? template.trim() : '';
  const safeTemplate = normalized.length <= 140 && normalized.includes('{count}') ? normalized : DEFAULT_MESSAGE;
  return safeTemplate.replaceAll('{count}', String(count));
}

function createSessionId() {
  const bytes = new Uint8Array(18);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function getSessionId() {
  const key = 'storefront-product-presence-session';
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const sessionId = createSessionId();
    window.sessionStorage.setItem(key, sessionId);
    return sessionId;
  } catch (_error) {
    return createSessionId();
  }
}

/**
 * Small, shared storefront component used by every product-page theme.
 * It makes one pulse while the page is visible and then at most one every
 * 30 seconds. The backend fails closed if Redis, the merchant toggle, or the
 * Premium entitlement is unavailable.
 */
const ProductViewerCount = ({ storeSlug, productId, enabled, message }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled || !storeSlug || !productId) {
      setCount(0);
      return undefined;
    }

    const sessionId = getSessionId();
    let cancelled = false;
    let timer;

    const pulse = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const response = await axios.post(
          `/storefront/${encodeURIComponent(storeSlug)}/products/${encodeURIComponent(productId)}/viewer-presence`,
          { sessionId },
        );
        if (!cancelled) setCount(response.data?.enabled ? Number(response.data.count) || 0 : 0);
      } catch (_error) {
        if (!cancelled) setCount(0);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') pulse();
    };

    pulse();
    timer = window.setInterval(pulse, PULSE_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, productId, storeSlug]);

  if (count < 2) return null;

  return (
    <p className="product-viewer-count" role="status" aria-live="polite">
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12S5.25 5.25 12 5.25 21.75 12 21.75 12 18.75 18.75 12 18.75 2.25 12 2.25 12Z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      {formatMessage(message, count)}
    </p>
  );
};

export default ProductViewerCount;
